import { useState, useCallback, useEffect } from 'react'

interface GraphMember {
  displayName: string
  mail: string | null
  userPrincipalName: string
}

interface ScheduleConfig {
  activeHoursStart: number
  activeHoursEnd: number
  timezone: string
}

interface LaunchRequest {
  projectName: string
  repoUrl: string
  branch: string
  intent: string
  schedule?: ScheduleConfig
  members?: string[]
  residency?: 'au' | 'none'
}

interface ProjectLauncherProps {
  /** May return a promise; a rejection's message is shown under the button (L3A2-5). */
  onLaunch: (request: LaunchRequest) => void | Promise<void>
  isLaunching?: boolean
  graphToken?: string | null
  graphTokenError?: string | null
}

export function ProjectLauncher({ onLaunch, isLaunching = false, graphToken, graphTokenError }: ProjectLauncherProps) {
  const [projectName, setProjectName] = useState('')
  const [repoUrl, setRepoUrl] = useState('')
  const [branch, setBranch] = useState('main')
  const [intent, setIntent] = useState('')
  const [overnightMode, setOvernightMode] = useState(false)
  const [activeHoursStart, setActiveHoursStart] = useState(22)
  const [activeHoursEnd, setActiveHoursEnd] = useState(6)
  const [members, setMembers] = useState<string[]>([])
  const [residency, setResidency] = useState<'au' | 'none'>('none')
  const [groupMembers, setGroupMembers] = useState<GraphMember[]>([])
  const [graphLoaded, setGraphLoaded] = useState(false)
  const [graphLoading, setGraphLoading] = useState(false)
  const [graphError, setGraphError] = useState<string | null>(null)
  const [selectedGroupMember, setSelectedGroupMember] = useState('')
  const [launchError, setLaunchError] = useState<string | null>(null)

  // Fetch group members from Graph API
  useEffect(() => {
    const groupId = import.meta.env.VITE_SECURITY_GROUP_ID
    if (!groupId || !graphToken) return

    setGraphLoading(true)
    setGraphError(null)

    fetch(`https://graph.microsoft.com/v1.0/groups/${groupId}/members`, {
      headers: { Authorization: `Bearer ${graphToken}` },
    })
      .then(async (response) => {
        if (!response.ok) {
          const body = await response.text().catch(() => '')
          throw new Error(`Graph API ${response.status}: ${body.slice(0, 200)}`)
        }
        return response.json()
      })
      .then((data) => {
        const result: GraphMember[] = data.value || []
        setGroupMembers(result)
        setGraphLoaded(true)
        if (result.length === 0) {
          setGraphError('Security group has no members')
        }
      })
      .catch((err) => {
        console.error('Graph API call failed:', err)
        setGraphError(err instanceof Error ? err.message : 'Failed to load directory')
      })
      .finally(() => setGraphLoading(false))
  }, [graphToken])

  const availableGroupMembers = groupMembers.filter(
    gm => !members.some(m => m.toLowerCase() === (gm.mail || gm.userPrincipalName).toLowerCase())
  )

  const handleAddMember = (email: string) => {
    if (!email.trim()) return
    const normalized = email.trim().toLowerCase()
    if (members.some(m => m.toLowerCase() === normalized)) return
    setMembers(prev => [...prev, email.trim()])
    setSelectedGroupMember('')
  }

  const handleRemoveMember = (email: string) => {
    setMembers(prev => prev.filter(m => m !== email))
  }

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    if (!projectName.trim() || !intent.trim()) return

    const request: LaunchRequest = {
      projectName: projectName.trim(),
      repoUrl: repoUrl.trim(),
      branch: branch.trim() || 'main',
      intent: intent.trim(),
      residency,
    }

    if (overnightMode) {
      request.schedule = {
        activeHoursStart,
        activeHoursEnd,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }
    }

    if (members.length > 0) {
      request.members = members
    }

    setLaunchError(null)
    const showError = (err: unknown) => setLaunchError(err instanceof Error ? err.message : String(err))
    try {
      Promise.resolve(onLaunch(request)).catch(showError)
    } catch (err) {
      showError(err)
    }
  }, [projectName, repoUrl, branch, intent, overnightMode, activeHoursStart, activeHoursEnd, members, residency, onLaunch])

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-6 bg-cosmic-abyss rounded-xl border border-cosmic-border max-w-lg mx-auto">
      <h2 className="text-lg font-bold text-white">Launch Project</h2>

      <div>
        <label className="block text-sm text-gray-400 mb-1">Project Name</label>
        <input
          type="text"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          placeholder="my-project"
          className="w-full px-3 py-2 bg-cosmic-depth border border-cosmic-border rounded-lg text-white placeholder-gray-600 focus:border-cosmic-nebula focus:outline-none"
          required
        />
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-1">Repository URL</label>
        <input
          type="text"
          value={repoUrl}
          onChange={(e) => setRepoUrl(e.target.value)}
          placeholder="https://github.com/org/repo"
          className="w-full px-3 py-2 bg-cosmic-depth border border-cosmic-border rounded-lg text-white placeholder-gray-600 focus:border-cosmic-nebula focus:outline-none"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-1">Branch</label>
        <input
          type="text"
          value={branch}
          onChange={(e) => setBranch(e.target.value)}
          placeholder="main"
          className="w-full px-3 py-2 bg-cosmic-depth border border-cosmic-border rounded-lg text-white placeholder-gray-600 focus:border-cosmic-nebula focus:outline-none"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-1">Intent</label>
        <textarea
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
          placeholder="Describe what you want to build..."
          rows={3}
          className="w-full px-3 py-2 bg-cosmic-depth border border-cosmic-border rounded-lg text-white placeholder-gray-600 focus:border-cosmic-nebula focus:outline-none resize-none"
          required
        />
      </div>

      {/* Members */}
      {(graphToken || graphTokenError) && (
        <div className="border-t border-cosmic-border pt-4">
          <label className="block text-sm text-gray-400 mb-2">Project Members</label>

          {/* Current members */}
          {members.length > 0 && (
            <div className="space-y-1 mb-2">
              {members.map(email => (
                <div key={email} className="flex items-center justify-between py-1.5 px-3 bg-white/5 rounded-lg">
                  <span className="text-sm text-gray-200">{email}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveMember(email)}
                    className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add member dropdown */}
          {graphLoading ? (
            <div className="px-3 py-2 text-sm text-gray-500 bg-cosmic-depth border border-cosmic-border rounded-lg">
              Loading directory...
            </div>
          ) : graphError ? (
            <div className="px-3 py-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg">
              {graphError}
            </div>
          ) : availableGroupMembers.length > 0 ? (
            <div className="flex gap-2">
              <select
                value={selectedGroupMember}
                onChange={(e) => setSelectedGroupMember(e.target.value)}
                className="flex-1 appearance-none bg-cosmic-depth border border-cosmic-border rounded-lg px-3 py-2 text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-cosmic-nebula"
              >
                <option value="">Add a member...</option>
                {availableGroupMembers.map(gm => {
                  const email = gm.mail || gm.userPrincipalName
                  return (
                    <option key={email} value={email}>
                      {gm.displayName}
                    </option>
                  )
                })}
              </select>
              <button
                type="button"
                onClick={() => handleAddMember(selectedGroupMember)}
                disabled={!selectedGroupMember}
                className="px-3 py-2 text-sm font-medium text-white bg-cosmic-nebula hover:bg-cosmic-nebula/80 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                Add
              </button>
            </div>
          ) : graphLoaded ? (
            <p className="text-gray-500 text-sm italic">All directory members already added</p>
          ) : !graphToken && graphTokenError ? (
            <div className="px-3 py-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg">
              Directory error: {graphTokenError}
            </div>
          ) : null}

          <p className="text-xs text-gray-600 mt-1">
            Members who can view and manage this project.
          </p>
        </div>
      )}

      {/* Data Residency */}
      <div className="border-t border-cosmic-border pt-4">
        <span className="text-sm text-white font-medium">Data Residency</span>
        <p className="text-xs text-gray-500 mt-0.5 mb-3">
          Choose where model calls are routed. Residency is set at creation and cannot be changed later.
        </p>
        <div className="space-y-2">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="residency"
              value="none"
              checked={residency === 'none'}
              onChange={() => setResidency('none')}
              disabled={isLaunching}
              className="mt-0.5 accent-cosmic-nebula"
            />
            <span className="text-sm text-gray-200">
              Default <span className="text-gray-500">(US-hosted Anthropic OK)</span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="residency"
              value="au"
              checked={residency === 'au'}
              onChange={() => setResidency('au')}
              disabled={isLaunching}
              aria-describedby={residency === 'au' ? 'residency-au-warning' : undefined}
              className="mt-0.5 accent-cosmic-nebula"
            />
            <span className="text-sm text-gray-200">
              Australia <span className="text-gray-500">(AU-resident only)</span>
            </span>
          </label>
        </div>
        {residency === 'au' && (
          <div
            id="residency-au-warning"
            role="status"
            className="mt-3 px-3 py-2 text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg"
          >
            AU mode routes all model calls through Azure Foundry in Sydney.
            Quality may regress on reasoning-heavy personas — see the design doc for details.
            Residency is set at creation and cannot be changed later.
          </div>
        )}
      </div>

      {/* Overnight Mode Toggle */}
      <div className="border-t border-cosmic-border pt-4">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm text-white font-medium">Overnight Mode</span>
            <p className="text-xs text-gray-500 mt-0.5">
              Run heavy work during off-peak hours to avoid daytime plan usage
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOvernightMode(!overnightMode)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              overnightMode ? 'bg-cosmic-nebula' : 'bg-cosmic-surface'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                overnightMode ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {overnightMode && (
          <div className="mt-3 flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Active From</label>
              <select
                value={activeHoursStart}
                onChange={(e) => setActiveHoursStart(parseInt(e.target.value, 10))}
                className="w-full px-3 py-2 bg-cosmic-depth border border-cosmic-border rounded-lg text-white focus:border-cosmic-nebula focus:outline-none text-sm"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>
                    {i.toString().padStart(2, '0')}:00
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Active Until</label>
              <select
                value={activeHoursEnd}
                onChange={(e) => setActiveHoursEnd(parseInt(e.target.value, 10))}
                className="w-full px-3 py-2 bg-cosmic-depth border border-cosmic-border rounded-lg text-white focus:border-cosmic-nebula focus:outline-none text-sm"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>
                    {i.toString().padStart(2, '0')}:00
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {overnightMode && (
          <p className="text-xs text-gray-600 mt-2">
            Agents will only run between {activeHoursStart.toString().padStart(2, '0')}:00 and {activeHoursEnd.toString().padStart(2, '0')}:00 ({Intl.DateTimeFormat().resolvedOptions().timeZone})
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={isLaunching || !projectName.trim() || !intent.trim()}
        className="w-full px-4 py-2.5 bg-cosmic-nebula hover:bg-cosmic-nebula-light disabled:bg-cosmic-surface disabled:text-gray-600 text-white font-medium rounded-lg transition-colors"
      >
        {isLaunching ? 'Launching...' : 'Launch Project'}
      </button>

      {launchError && (
        <div
          role="alert"
          className="px-3 py-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg"
        >
          Launch failed: {launchError}
        </div>
      )}
    </form>
  )
}
